/** Shared half-hour / quarter-hour timeline slot helpers. */

export function slotsPerDay(slotMinutes: number): number {
  return Math.floor((24 * 60) / slotMinutes);
}

export function minuteToSlot(minute: number, slotMinutes: number): number {
  return Math.floor(minute / slotMinutes);
}

export function slotToMinute(slot: number, slotMinutes: number): number {
  return slot * slotMinutes;
}

export function formatHm(minute: number): string {
  const m = ((minute % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function parseHmToMinute(hm: string): number {
  const [h, min] = hm.split(':').map((x) => Number(x));
  if (![h, min].every((n) => Number.isFinite(n))) return 0;
  return h * 60 + min;
}

export type TimeSegment = { startMinute: number; endMinute: number };

/** Normalize selection so start < end; if equal, expand one slot. */
export function normalizeSlotRange(
  a: number,
  b: number,
  slotMinutes: number,
): { startMinute: number; endMinute: number; startSlot: number; endSlotExclusive: number } {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const startSlot = lo;
  const endSlotExclusive = hi + 1;
  return {
    startSlot,
    endSlotExclusive,
    startMinute: slotToMinute(startSlot, slotMinutes),
    endMinute: slotToMinute(endSlotExclusive, slotMinutes),
  };
}

/** Merge overlapping/adjacent segments sorted by start. */
export function mergeSegments(segments: TimeSegment[]): TimeSegment[] {
  if (segments.length === 0) return [];
  const sorted = [...segments].sort((x, y) => x.startMinute - y.startMinute);
  const out: TimeSegment[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = out[out.length - 1];
    const cur = sorted[i];
    if (cur.startMinute <= last.endMinute) {
      last.endMinute = Math.max(last.endMinute, cur.endMinute);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/** Replace overlapping region with a new segment (or clear if null). */
export function paintSegment(
  existing: TimeSegment[],
  startMinute: number,
  endMinute: number,
  paint: TimeSegment | null,
): TimeSegment[] {
  const kept = existing.filter((s) => s.endMinute <= startMinute || s.startMinute >= endMinute);
  const clipped: TimeSegment[] = [];
  for (const s of existing) {
    if (s.endMinute <= startMinute || s.startMinute >= endMinute) continue;
    if (s.startMinute < startMinute) clipped.push({ startMinute: s.startMinute, endMinute: startMinute });
    if (s.endMinute > endMinute) clipped.push({ startMinute: endMinute, endMinute: s.endMinute });
  }
  const next = [...kept, ...clipped];
  if (paint) next.push(paint);
  return mergeSegments(next);
}

/** True if [slotStart, slotEnd) overlaps any segment. */
export function slotCovered(
  startMinute: number,
  endMinute: number,
  segments: TimeSegment[],
): boolean {
  return segments.some((s) => s.startMinute < endMinute && s.endMinute > startMinute);
}

export function hourLabels(slotMinutes: number): Array<{ hour: number; slot: number }> {
  const labels: Array<{ hour: number; slot: number }> = [];
  for (let h = 0; h < 24; h++) {
    labels.push({ hour: h, slot: minuteToSlot(h * 60, slotMinutes) });
  }
  return labels;
}
